import { useState } from "react"
import { Link } from "react-router-dom"
import { LoversApi } from "api/LoversApi"
import { EnvelopeIcon } from "@heroicons/react/24/outline"
import { LoversLiveForeverLogo } from "components/LoversLiveForeverLogo"
import { Alert, Button, TextField } from "components/ui"
import { AxiosError } from "axios"

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(undefined)

    try {
      await LoversApi().forgotPassword.sendForgotPasswordMail({ email })
      setIsSuccess(true)
    } catch (err: unknown | AxiosError) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 400) {
          setError("Please enter a valid email address")
        } else {
          setError(err.response?.data?.errorMessage || "Something went wrong")
        }
      } else {
        setError("Something went wrong")
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <Link to="/">
            <LoversLiveForeverLogo className="mx-auto h-20 w-auto" />
          </Link>
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
            Check your email
          </h2>
          <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
            If an account exists with {email}, you will receive a password reset link.
          </p>
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-primary-600 hover:text-primary-500"
            >
              Return to login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Link to="/">
          <LoversLiveForeverLogo className="mx-auto h-20 w-auto" />
        </Link>
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <TextField
            label="Email address"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => {
              if (error) setError(undefined)
              setEmail(e.target.value)
            }}
            placeholder="you@example.com"
            leadingIcon={<EnvelopeIcon className="h-5 w-5" aria-hidden="true" />}
          />

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <div>
            <Button type="submit" fullWidth isLoading={isLoading}>
              {isLoading ? "Sending..." : "Send reset link"}
            </Button>
          </div>
        </form>

        <p className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
          Remember your password?{" "}
          <Link
            to="/login"
            className="font-semibold leading-6 text-primary-600 hover:text-primary-500"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
